import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Calculator,
  RotateCcw,
  Check,
  X,
  Sparkles,
  Info,
  Lock,
  Unlock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useEquipStore,
  RoleType,
  Quarter,
  MeasureState,
  measureNames,
  roleNames,
  getMaxAward,
  calculateAward,
  isPhysicianRole,
  AwardResult,
} from '@/store/equipStore';
import { useEquipMeasureScheduleYear } from '@/hooks/equip/useEquipMeasureScheduleYear';
import {
  getMeasureThresholdDisplay,
  getRoleQuarterOutcomeMeasures,
  type ScheduleRole,
} from '@/lib/measureScheduleSelectors';

/** Maps selector frontend measure names → store measure IDs.
 *  Includes both translated names (PHQ-9) and DB passthroughs (PHQ9, ACO_Visits, etc.)
 *  to handle any measures not covered by SQL_TO_FRONTEND_MEASURE in the selector. */
const SCHEDULE_NAME_TO_ID: Record<string, string> = {
  'A1C': 'a1c',
  'AWV': 'awv',
  'Colorectal': 'colorectal',
  'CRCS': 'colorectal',       // DB passthrough fallback
  'PHQ-9': 'phq9',
  'PHQ9': 'phq9',             // DB passthrough fallback
  'BCS': 'bcs',
  'HTN': 'htn',
  'ACP': 'acp',
  'ACO': 'aco',
  'ACO_Visits': 'aco',        // DB passthrough fallback
  'EDSendOut': 'ed-sendout',
  'ED_SendOut': 'ed-sendout', // DB passthrough fallback
};

/** Map store RoleType to ScheduleRole (physician IDs differ between the two). */
function toScheduleRole(role: RoleType): ScheduleRole {
  if (role === 'primary-care-md') return 'primary-care-physician';
  if (role === 'psychiatry-md') return 'psychiatry-physician';
  return role as ScheduleRole;
}

interface PillarCalculatorProps {
  /** Only show roles for this track */
  track: 'physician' | 'non-physician';
  /** Lock the calculator to a specific role (equip.read persona). Hides the role selector. */
  lockedRole?: RoleType;
  /** When set, initializes the calculator to this employment status and hides the toggle for FT users. */
  effectiveEmploymentStatus?: import('@/data/types').EmploymentStatus;
  className?: string;
}

const physicianRoles: RoleType[] = ['primary-care-md', 'psychiatry-md'];
const apcRoles: RoleType[] = ['primary-care-apc', 'psychiatry-apc', 'palliative-care-apc', 'teletriage'];

const quarters: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];

interface GateSectionProps {
  gatePassed: boolean;
  onToggle: () => void;
  quarter: Quarter;
  thresholdLabel: string;
}

function GateSection({ gatePassed, onToggle, quarter: _quarter, thresholdLabel }: GateSectionProps): React.ReactElement {
  const threshold = thresholdLabel;

  return (
    <Card className={cn(
      'mb-6 border-2',
      gatePassed ? 'border-green-500 bg-green-50' : 'border-amber-500 bg-amber-50'
    )}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {gatePassed ? (
              <Unlock className="w-6 h-6 text-green-600" />
            ) : (
              <Lock className="w-6 h-6 text-amber-600" />
            )}
            <div>
              <h3 className="font-semibold text-lg">ACO Visits Eligibility Requirement</h3>
              <p className="text-sm text-gray-600">Threshold: {threshold}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={gatePassed ? undefined : onToggle}
              className={cn(
                'px-4 py-2 rounded-md font-medium transition-colors',
                gatePassed
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              )}
            >
              Met
            </button>
            <button
              onClick={gatePassed ? onToggle : undefined}
              className={cn(
                'px-4 py-2 rounded-md font-medium transition-colors',
                !gatePassed
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              )}
            >
              Unmet
            </button>
          </div>
        </div>

        {!gatePassed && (
          <div className="mt-3 p-3 bg-amber-100 rounded-md">
            <p className="text-amber-800 text-sm">
              Eligibility requirement not met. Meet your ACO Visits target to unlock your incentive opportunity.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function PillarCalculator({ track, lockedRole, effectiveEmploymentStatus, className }: PillarCalculatorProps) {
  const {
    calculator,
    setRole,
    setQuarter,
    setEmploymentStatus,
    setDaysPerWeek,
    toggleMeasure,
    toggleGate,
    resetMeasures,
    initializeMeasuresForRole,
    setOutcomeMeasures,
  } = useEquipStore();

  const { items: scheduleItems, isLoading: scheduleLoading, isError: scheduleError } = useEquipMeasureScheduleYear(new Date().getFullYear());

  const roles = track === 'physician' ? physicianRoles : apcRoles;
  const defaultRole = track === 'physician' ? 'primary-care-md' : 'primary-care-apc';
  const scheduleRole = toScheduleRole(calculator.role);

  // Initialize calculator with persona-locked role or track default on mount
  useEffect(() => {
    initializeMeasuresForRole(lockedRole ?? defaultRole, 'Q1');
    setEmploymentStatus(effectiveEmploymentStatus ?? 'full-time');
  }, [track, lockedRole, defaultRole, effectiveEmploymentStatus, initializeMeasuresForRole, setEmploymentStatus]);

  // Stable key: only changes when the actual set of outcome IDs changes.
  // Avoids infinite loop from scheduleItems being a new array reference on every render.
  const outcomeIdsKey = React.useMemo(() => {
    if (scheduleLoading || scheduleError || !scheduleItems.length) return '';
    const names = getRoleQuarterOutcomeMeasures(scheduleItems, scheduleRole, calculator.quarter);
    return names.map(n => SCHEDULE_NAME_TO_ID[n]).filter(Boolean).join(',');
  }, [scheduleItems, scheduleLoading, scheduleError, scheduleRole, calculator.quarter]);

  // Sync outcome measures from DB schedule when the derived ID set changes
  useEffect(() => {
    if (!outcomeIdsKey) return;
    setOutcomeMeasures(outcomeIdsKey.split(',').map(id => ({ id, passed: true })));
  }, [outcomeIdsKey, setOutcomeMeasures]);

  const maxAward = getMaxAward(calculator.role, calculator.employmentStatus, calculator.daysPerWeek);
  const result = calculateAward(
    calculator.outcomeMeasures,
    calculator.consistencyMeasures,
    maxAward,
    calculator.role,
    calculator.quarter,
    calculator.gatePassed
  );

  // Check if current role is a physician (has gate requirement)
  const isPhysician = isPhysicianRole(calculator.role);
  const isQ1 = calculator.quarter === 'Q1';

  // Derive ACO gate threshold label from schedule (display value is 0–100)
  const acoThresholdDisplay = (!scheduleLoading && !scheduleError)
    ? getMeasureThresholdDisplay(scheduleItems, scheduleRole, 'ACO', calculator.quarter)
    : undefined;
  const gateThresholdLabel = acoThresholdDisplay !== undefined ? `${acoThresholdDisplay}%` : '90%';

  // Check if current role has single-measure risk (only for non-Q1 or non-physician)
  const isSingleMeasureRisk = calculator.outcomeMeasures.length === 1 && !(isPhysician && isQ1);

  // Check if ED Send-Out (inverted threshold)
  const hasInvertedMeasure = calculator.outcomeMeasures.some(m => m.id === 'ed-sendout');

  return (
    <Card className={cn('overflow-hidden border-2', className)}>
      <CardHeader className="bg-gradient-to-r from-eventus-purple/10 via-eventus-teal/5 to-eventus-navy/10 p-6 pb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-white shadow-sm">
              <Calculator className="w-6 h-6 text-eventus-purple" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-eventus-navy">
                EQuIP Incentive Predictor
              </CardTitle>
              <p className="text-base text-gray-600 mt-1">
                Click any measure to toggle measure met/unmet
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={resetMeasures}
            className="gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
        </div>

        {/* Controls Section - with consistent internal padding */}
        <div className="mt-6 bg-white/60 rounded-xl p-4 border border-gray-200/50">
          {/* Controls Row */}
          <div className="flex flex-wrap items-end gap-6">
            {/* Role Selector */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Role</label>
              {lockedRole ? (
                <div className="flex items-center gap-1.5 h-10 px-3 bg-white rounded-lg border border-gray-200 text-sm text-gray-700 font-medium min-w-[180px]">
                  <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span>{roleNames[lockedRole]}</span>
                </div>
              ) : (
                <Select
                  value={calculator.role}
                  onValueChange={(v) => setRole(v as RoleType)}
                >
                  <SelectTrigger className="bg-white w-auto min-w-[180px] h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {roleNames[role]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Quarter Toggle */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Quarter</label>
              <div className="flex gap-0.5 bg-white rounded-lg p-1 border h-10 items-center">
                {quarters.map((q) => (
                  <button
                    key={q}
                    onClick={() => setQuarter(q)}
                    className={cn(
                      'px-3 py-1 rounded-md text-base font-medium transition-all',
                      calculator.quarter === q
                        ? 'bg-eventus-purple text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    )}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Employment Status - Segmented Control (hidden when locked to a specific status) */}
            {!effectiveEmploymentStatus && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Employment</label>
                <div className="flex gap-0.5 bg-white rounded-lg p-1 border h-10 items-center">
                  <button
                    onClick={() => setEmploymentStatus('full-time')}
                    className={cn(
                      'px-3 py-1 rounded-md text-base font-medium transition-all',
                      calculator.employmentStatus === 'full-time'
                        ? 'bg-eventus-purple text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    )}
                  >
                    Full-Time
                  </button>
                  <button
                    onClick={() => setEmploymentStatus('part-time')}
                    className={cn(
                      'px-3 py-1 rounded-md text-base font-medium transition-all',
                      calculator.employmentStatus === 'part-time'
                        ? 'bg-eventus-purple text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    )}
                  >
                    Part-Time
                  </button>
                </div>
              </div>
            )}

            {/* Days/Hours per week slider (only for part-time) */}
            {calculator.employmentStatus === 'part-time' && (() => {
              const isTeletriage = calculator.role === 'teletriage';
              return (
                <div className="space-y-1.5 w-[280px]">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {isTeletriage
                      ? `Hrs/Week: ${calculator.daysPerWeek}`
                      : `Days/Week: ${calculator.daysPerWeek}`}
                  </label>
                  <div className="h-10 flex items-center">
                    <Slider
                      value={[calculator.daysPerWeek]}
                      onValueChange={([v]) => setDaysPerWeek(v)}
                      min={isTeletriage ? 8 : 0}
                      max={isTeletriage ? 39 : 5}
                      step={isTeletriage ? 1 : 0.01}
                      className="w-full"
                    />
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        {/* Gate Section for Physicians */}
        {isPhysician && (
          <GateSection
            gatePassed={calculator.gatePassed}
            onToggle={toggleGate}
            quarter={calculator.quarter}
            thresholdLabel={gateThresholdLabel}
          />
        )}

        {/* Notice for single-measure focus */}
        {isSingleMeasureRisk && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3"
          >
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Single Outcome Measure</p>
              <p className="text-base text-blue-700 mt-1">
                This role has one focused outcome measure representing 60% of the incentive opportunity.
              </p>
            </div>
          </motion.div>
        )}

        {/* Measures Section - Unified row with all measures */}
        <div className={cn(!calculator.gatePassed && isPhysician && 'opacity-50 pointer-events-none')}>
          {/* Section Headers */}
          <div className="flex items-end gap-4 mb-4">
            {/* Only show Outcome header if there are outcome measures */}
            {calculator.outcomeMeasures.length > 0 && (
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-eventus-navy">
                    Outcome Measures
                  </h3>
                  <span className="text-base text-gray-500">({result.outcomeWeight}%)</span>
                  {hasInvertedMeasure && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                      ED Send-Out: &lt;25%
                    </span>
                  )}
                </div>
              </div>
            )}
            <div className={calculator.outcomeMeasures.length > 0 ? 'flex-shrink-0' : 'flex-1'}>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-eventus-navy">
                  Consistency Measures
                </h3>
                <span className="text-base text-gray-500">({result.consistencyWeight}%)</span>
              </div>
            </div>
          </div>

          {/* Unified Measure Cards Row */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${calculator.role}-${calculator.quarter}-all-measures`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="flex gap-2"
            >
              {/* Outcome Measures (only if present) */}
              {calculator.outcomeMeasures.map((measure) => (
                <MeasurePillar
                  key={measure.id}
                  measure={measure}
                  type="outcome"
                  totalMeasures={calculator.outcomeMeasures.length + calculator.consistencyMeasures.length}
                  maxAward={maxAward}
                  isInverted={measure.id === 'ed-sendout'}
                  onClick={() => toggleMeasure(measure.id, 'outcome')}
                  poolPercentage={result.outcomeWeight}
                  poolMeasureCount={calculator.outcomeMeasures.length}
                  disabled={!calculator.gatePassed && isPhysician}
                />
              ))}

              {/* Visual Divider (only if outcome measures exist) */}
              {calculator.outcomeMeasures.length > 0 && (
                <div className="w-1 bg-gray-200 rounded-full flex-shrink-0 my-2" />
              )}

              {/* Consistency Measures */}
              {calculator.consistencyMeasures.map((measure) => (
                <MeasurePillar
                  key={measure.id}
                  measure={measure}
                  type="consistency"
                  totalMeasures={calculator.outcomeMeasures.length + calculator.consistencyMeasures.length}
                  maxAward={maxAward}
                  onClick={() => toggleMeasure(measure.id, 'consistency')}
                  poolPercentage={result.consistencyWeight}
                  poolMeasureCount={calculator.consistencyMeasures.length}
                  disabled={!calculator.gatePassed && isPhysician}
                />
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Summary Row */}
          <div className="mt-3 flex items-center gap-4">
            {/* Only show outcome summary if there are outcome measures */}
            {calculator.outcomeMeasures.length > 0 && (
              <>
                <div className="flex-1 flex items-center justify-start gap-6 text-base">
                  <span className="text-gray-600">
                    {result.outcomePercentage.toFixed(1)}% of {result.outcomeWeight}%
                  </span>
                  <span className="font-semibold text-pink-600">
                    ${result.outcomeAward.toLocaleString()}
                  </span>
                </div>
                <div className="w-1 flex-shrink-0" />
              </>
            )}
            <div className={cn(
              'flex items-center gap-6 text-base',
              calculator.outcomeMeasures.length > 0 ? 'flex-1 justify-end' : 'flex-1 justify-center'
            )}>
              <span className="text-gray-600">
                {result.consistencyPercentage.toFixed(1)}% of {result.consistencyWeight}%
              </span>
              <span className="font-semibold text-blue-600">
                ${result.consistencyAward.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Results Panel - now below measures, full width */}
        <ResultsPanel
          maxAward={maxAward}
          result={result}
          isPhysician={isPhysician}
          quarter={calculator.quarter}
        />
      </CardContent>
    </Card>
  );
}

interface MeasurePillarProps {
  measure: MeasureState;
  type: 'outcome' | 'consistency';
  totalMeasures: number;
  maxAward: number;
  isInverted?: boolean;
  onClick: () => void;
  poolPercentage?: number;
  poolMeasureCount?: number;
  disabled?: boolean;
}

function MeasurePillar({
  measure,
  type,
  totalMeasures,
  maxAward,
  isInverted = false,
  onClick,
  poolPercentage: poolPct,
  poolMeasureCount,
  disabled = false,
}: MeasurePillarProps) {
  // Use provided pool values or fall back to defaults
  const poolPercentage = poolPct ?? (type === 'outcome' ? 60 : 40);
  const measuresInPool = poolMeasureCount ?? totalMeasures;
  const measurePercentage = poolPercentage / measuresInPool;
  const measureDollar = Math.round((measurePercentage / 100) * maxAward);

  const baseColor = type === 'outcome' ? 'bg-pink-500' : 'bg-blue-500';
  const hoverColor = type === 'outcome' ? 'hover:bg-pink-600' : 'hover:bg-blue-600';
  const missedColor = 'bg-gray-200';
  const missedHoverColor = 'hover:bg-gray-300';

  return (
    <motion.button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        'relative flex flex-col items-center justify-end rounded-lg p-3 transition-all flex-1 min-w-0',
        'border-2 border-transparent',
        disabled
          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
          : measure.passed
            ? `${baseColor} ${hoverColor} text-white cursor-pointer`
            : `${missedColor} ${missedHoverColor} text-gray-600 cursor-pointer`,
        measure.passed && !disabled && 'shadow-md',
        !measure.passed && !disabled && 'opacity-80'
      )}
      style={{ minHeight: 130 }}
      whileHover={disabled ? undefined : { scale: 1.02 }}
      whileTap={disabled ? undefined : { scale: 0.98 }}
      layout
    >
      {/* Met/Unmet Icon */}
      <div className={cn(
        'absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center',
        disabled ? 'bg-gray-300/30' : measure.passed ? 'bg-white/20' : 'bg-gray-400/20'
      )}>
        {measure.passed ? (
          <Check className="w-3 h-3" />
        ) : (
          <X className="w-3 h-3" />
        )}
      </div>

      {/* Measure Name */}
      <span className="text-xs font-medium text-center leading-tight mt-6 px-1">
        {measureNames[measure.id]}
        {isInverted && <span className="block text-[10px] opacity-75">(&lt;25%)</span>}
      </span>

      {/* Percentage */}
      <span className="text-lg font-bold mt-1">
        {measurePercentage === 0
          ? <span className="text-xs font-semibold opacity-75">Gate Measure</span>
          : `${measurePercentage.toFixed(measurePercentage % 1 === 0 ? 0 : 1)}%`}
      </span>

      {/* Dollar Value */}
      <span className={cn(
        'text-xs mt-1',
        disabled ? 'opacity-40' : measure.passed ? 'opacity-90' : 'opacity-60'
      )}>
        {disabled ? '$0' : measure.passed ? `$${measureDollar.toLocaleString()}` : '$0'}
      </span>
    </motion.button>
  );
}

interface ResultsPanelProps {
  maxAward: number;
  result: AwardResult;
  isPhysician: boolean;
  quarter: Quarter;
}

function ResultsPanel({ maxAward, result, isPhysician, quarter }: ResultsPanelProps) {
  const isQ1 = quarter === 'Q1';
  const gateUnmet = isPhysician && !result.gatePassed;

  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <h3 className="text-lg font-semibold text-eventus-navy mb-4">
        Your Results
      </h3>

      {/* Award Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Award Breakdown */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-2">
            {/* Gate Status for Physicians */}
            {isPhysician && (
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-base text-gray-600">Eligibility Status</span>
                <span className={cn(
                  'font-semibold px-2 py-0.5 rounded text-sm',
                  result.gatePassed
                    ? 'bg-green-100 text-green-700'
                    : 'bg-amber-100 text-amber-700'
                )}>
                  {result.gatePassed ? 'Met' : 'Unmet'}
                </span>
              </div>
            )}

            {/* Only show outcome row if there are outcome measures (Q2+) */}
            {!isQ1 || !isPhysician ? (
              <div className="flex justify-between items-center">
                <span className="text-base text-gray-600">
                  Outcome Award
                  {isPhysician && <span className="text-xs text-gray-400 ml-1">({result.outcomeWeight}%)</span>}
                </span>
                <motion.span
                  key={result.outcomeAward}
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  className={cn('font-semibold', gateUnmet ? 'text-gray-400' : 'text-pink-600')}
                >
                  ${result.outcomeAward.toLocaleString()}
                </motion.span>
              </div>
            ) : null}

            <div className="flex justify-between items-center">
              <span className="text-base text-gray-600">
                Consistency Award
                {isPhysician && <span className="text-xs text-gray-400 ml-1">({result.consistencyWeight}%)</span>}
              </span>
              <motion.span
                key={result.consistencyAward}
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                className={cn('font-semibold', gateUnmet ? 'text-gray-400' : 'text-blue-600')}
              >
                ${result.consistencyAward.toLocaleString()}
              </motion.span>
            </div>
            <div className="pt-2 border-t border-gray-200 flex justify-between items-center">
              <span className="text-base font-medium text-gray-700">Quarterly Award</span>
              <motion.span
                key={result.totalAward}
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                className={cn('text-xl font-bold', gateUnmet ? 'text-gray-400' : 'text-eventus-navy')}
              >
                ${result.totalAward.toLocaleString()}
              </motion.span>
            </div>
            <div className="text-xs text-gray-500 text-right">
              of ${maxAward.toLocaleString()} maximum
            </div>
          </div>
        </div>

        {/* Achievement Badge or Status */}
        <div className="p-4 rounded-lg border-2 flex items-center justify-center">
          <AnimatePresence mode="wait">
            {gateUnmet ? (
              <motion.div
                key="gate-unmet"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-center bg-amber-50 rounded-lg p-4 w-full"
              >
                <Lock className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                <div className="font-bold text-lg text-amber-800">
                  Eligibility Requirement Unmet
                </div>
                <div className="text-base text-amber-700 mt-1">
                  Meet your ACO Visits target to unlock your incentive opportunity
                </div>
              </motion.div>
            ) : result.totalPercentage >= 100 ? (
              <motion.div
                key="achieved"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-center bg-gradient-to-r from-eventus-purple/10 to-eventus-teal/10 rounded-lg p-4 w-full"
              >
                <Sparkles className="w-8 h-8 mx-auto text-eventus-purple mb-2" />
                <div className="font-bold text-lg text-eventus-navy">
                  Maximum Award Achieved!
                </div>
                <div className="text-base text-gray-600">
                  {isPhysician && isQ1 ? 'All consistency measures met' : 'All measures met'}
                </div>
              </motion.div>
            ) : result.outcomePercentage === 0 && result.consistencyPercentage > 0 && !isPhysician ? (
              <motion.div
                key="floor"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-center bg-blue-50 rounded-lg p-4 w-full"
              >
                <div className="font-bold text-blue-800">
                  Consistency Floor Active
                </div>
                <div className="text-base text-blue-600 mt-1">
                  ${result.consistencyAward.toLocaleString()} protected even with all outcomes unmet
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="partial"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-center p-4 w-full"
              >
                <div className="text-4xl font-bold text-eventus-navy mb-1">
                  <span className="text-eventus-purple">$</span>
                  {result.totalAward.toLocaleString()}
                </div>
                <div className="text-base text-gray-500">
                  {result.totalPercentage.toFixed(0)}% of maximum
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
